import { OrganizationInviteClient } from '@frontend/account/api';
import { AuthenticationManager } from '@frontend/authentication-v2';
import { setCookie } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { getCorrectLocale } from '@frontend/lang';
import { ToastUtil } from '@frontend/toast-utils';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface AcceptAccountInviteFormValues {
    account_name: string;
    first_name: string;
    last_name: string;
    password: string;
    confirm_password: string;
}

export const useAcceptAccountInvite = () => {
    const { hash } = useParams<{ hash: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const intl = useIntl();

    const [formValues, setFormValues] = useState<AcceptAccountInviteFormValues>({
        account_name: '',
        first_name: '',
        last_name: '',
        password: '',
        confirm_password: ''
    });
    const [passwordValid, setPasswordValid] = useState(false);
    const [confirmPasswordValid, setConfirmPasswordValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isValidHash = useMemo(() => {
        if (!hash) return false;
        return UUID_REGEX.test(hash);
    }, [hash]);

    const passwordsMatch = formValues.password === formValues.confirm_password;

    const submitDisabled = useMemo(
        () =>
            isSubmitting ||
            !formValues.account_name.trim() ||
            !formValues.first_name.trim() ||
            !formValues.last_name.trim() ||
            !passwordValid ||
            !confirmPasswordValid ||
            !formValues.password ||
            !formValues.confirm_password ||
            !passwordsMatch,
        [
            confirmPasswordValid,
            formValues.account_name,
            formValues.confirm_password,
            formValues.first_name,
            formValues.last_name,
            formValues.password,
            isSubmitting,
            passwordValid,
            passwordsMatch
        ]
    );

    useEffect(() => {
        const language = searchParams.get('language');
        if (!language) return;

        const locale = getCorrectLocale(language);
        setCookie('language', locale.valueOf(), 365);
        window.dispatchEvent(new CustomEvent('languageChanged'));
    }, [searchParams]);

    const onSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
        e?.preventDefault();
        if (!hash || submitDisabled) return;

        setIsSubmitting(true);
        try {
            const includeAccessToken = AuthenticationManager.getInstance().hasValidToken();
            await OrganizationInviteClient.acceptOrganizationInvite(
                {
                    hash,
                    account_name: formValues.account_name.trim(),
                    first_name: formValues.first_name.trim(),
                    last_name: formValues.last_name.trim(),
                    password: formValues.password
                },
                includeAccessToken
            );

            ToastUtil.success(
                intl.formatMessage({
                    id: 'platform.standalone.accept-account-invite.success-title',
                    description: 'Toast title shown after successfully accepting an account invite',
                    defaultMessage: 'Invitation accepted'
                }),
                intl.formatMessage({
                    id: 'platform.standalone.accept-account-invite.success-message',
                    description: 'Toast message shown after successfully accepting an account invite',
                    defaultMessage: 'Your organization has been created. You can now log in.'
                })
            );

            if (includeAccessToken) {
                await AuthenticationManager.getInstance().doLogout();
            }

            navigate('/');
        } catch (error) {
            ErrorHandler.handleError({ error: error as Error });
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        isValidHash,
        formValues,
        setFormValues,
        passwordValid,
        setPasswordValid,
        confirmPasswordValid,
        setConfirmPasswordValid,
        passwordsMatch,
        submitDisabled,
        isSubmitting,
        onSubmit
    };
};
